import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import {
  BackToChat,
  IconLock,
  IconQrGrid,
  IconTick,
  IconWarn,
  OpenChatButton,
  Wordmark,
} from '@/components/chrome';
import { ChatApiError, confirmPay, fetchPaySummary, pollPayStatus } from '@/lib/http';
import { openRazorpayCheckout, PaymentCancelledError } from '@/lib/razorpay';
import type { PaySummary } from '@/lib/types';

// After a "processing" confirm, poll for the async-issued ticket so the QR opens
// in place. BMRCL's on_confirm normally lands in a few seconds; give generous
// headroom, then fall back to the WhatsApp ticket.
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 20_000;

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(id);
      reject(new DOMException('aborted', 'AbortError'));
    });
  });

// pollForTicket resolves the view code once the ticket is issued, or null on
// timeout. Transient poll errors are swallowed so a blip doesn't drop the rider.
async function pollForTicket(token: string, signal: AbortSignal): Promise<string | null> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS, signal);
    try {
      const res = await pollPayStatus(token, signal);
      if (res.viewToken) return res.viewToken;
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      // transient (network / 5xx) — keep polling until the deadline
    }
  }
  return null;
}

// The pay token arrives in the URL (/pay?token=…), tapped from the WhatsApp
// confirm card. No login — the signed pay token is the sole credential.
export const Route = createFileRoute('/pay')({
  validateSearch: (search: Record<string, unknown>): { token: string } => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: PayPage,
});

type Phase = 'loading' | 'ready' | 'verifying' | 'issuing' | 'paid' | 'done' | 'error';

function PayPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('loading');
  const [summary, setSummary] = useState<PaySummary | null>(null);
  const [error, setError] = useState<Error | null>(null);
  // Guards a double-open if the effect re-runs.
  const opening = useRef(false);
  // Cancels the post-payment poll if the page unmounts mid-issue.
  const pollAbort = useRef<AbortController | null>(null);
  useEffect(() => () => pollAbort.current?.abort(), []);

  useEffect(() => {
    const ctrl = new AbortController();
    if (!token) {
      setError(new ChatApiError(400, 'missing token'));
      setPhase('error');
      return;
    }
    fetchPaySummary(token, ctrl.signal)
      .then((s) => {
        setSummary(s);
        setPhase(s.status === 'paid' ? 'paid' : 'ready');
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        setError(e instanceof Error ? e : new Error('failed to load'));
        setPhase('error');
      });
    return () => ctrl.abort();
  }, [token]);

  const pay = useCallback(async () => {
    if (!summary || opening.current) return;
    opening.current = true;
    try {
      const proof = await openRazorpayCheckout(summary, {}, 'WeeBee metro ticket');
      setPhase('verifying');
      const res = await confirmPay(token, proof.razorpay_payment_id, proof.razorpay_signature);
      if (res.viewToken) {
        // Ticket issued synchronously — hand off to the live QR view.
        void navigate({ to: '/t/$token', params: { token: res.viewToken } });
        return;
      }
      // Issued asynchronously (ONDC on_confirm): stay on this page and poll so the
      // QR opens here — no bounce back to WhatsApp.
      setPhase('issuing');
      pollAbort.current = new AbortController();
      const code = await pollForTicket(token, pollAbort.current.signal);
      if (code) {
        void navigate({ to: '/t/$token', params: { token: code } });
        return;
      }
      // Slow issuance — the WhatsApp ticket_ready message carries the /t link.
      setPhase('done');
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return; // unmounted
      if (e instanceof PaymentCancelledError) {
        setPhase('ready'); // let them try again
        return;
      }
      setError(e instanceof Error ? e : new Error('payment failed'));
      setPhase('error');
    } finally {
      opening.current = false;
    }
  }, [summary, token, navigate]);

  switch (phase) {
    case 'loading':
      return <PayLoading />;
    case 'ready':
      return <PayReady summary={summary} onPay={pay} />;
    case 'verifying':
      return <PayVerifying />;
    case 'issuing':
      return <PayIssuing />;
    case 'paid':
      return <PayPaid />;
    case 'done':
      return <PayDone />;
    case 'error':
    default:
      return <PayError error={error} />;
  }
}

/* ---- A1 Loading -------------------------------------------------------- */
function PayLoading() {
  return (
    <main className="app">
      <div className="topbar">
        <Wordmark />
      </div>
      <div className="state">
        <div className="spin" style={{ '--s': '30px' } as CSSProperties} />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div className="sk" style={{ width: 210, height: 12 }} />
          <div className="sk" style={{ width: 200, height: 70, marginTop: 6 }} />
          <div className="sk" style={{ width: 130, height: 11 }} />
        </div>
        <p className="sub muted" style={{ fontSize: 13 }}>
          Loading your fare…
        </p>
      </div>
    </main>
  );
}

/* ---- A2 Ready (the hero) ---------------------------------------------- */
function PayReady({ summary, onPay }: { summary: PaySummary | null; onPay: () => void }) {
  if (!summary) return null;
  const { intPart, paisePart } = fareParts(summary.amountPaise, summary.currency);
  return (
    <main className="app">
      <div className="topbar">
        <Wordmark />
        <span className="secure">
          <IconLock />
          Secure
        </span>
      </div>
      <div
        className="fill"
        style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}
      >
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          Fare to pay
        </div>
        <div className="serif" style={{ fontSize: 104, lineHeight: 0.82 }}>
          {intPart}
          <span style={{ fontSize: 46, color: 'var(--muted)', letterSpacing: 0 }}>{paisePart}</span>
        </div>
        <div className="serif-i" style={{ fontSize: 19, color: 'var(--muted)', marginTop: 16 }}>
          one-way · single passenger
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <button type="button" className="btn btn--primary" onClick={onPay}>
          <span>Pay</span>
          <span className="amt">{formatMoney(summary.amountPaise, summary.currency)}</span>
        </button>
        <div className="footnote">
          <span className="ln" />
          <span className="tx">
            Secure payment · your ticket opens
            <br />
            automatically after you pay
          </span>
          <span className="ln" />
        </div>
      </div>
    </main>
  );
}

/* ---- A3 Verifying ------------------------------------------------------ */
function PayVerifying() {
  return (
    <main className="app">
      <div className="topbar">
        <Wordmark />
        <span className="secure">
          <IconLock />
          Secure
        </span>
      </div>
      <div className="state">
        <div className="spin" style={{ '--s': '46px' } as CSSProperties} />
        <h3>
          Confirming
          <br />
          your payment…
        </h3>
        <p className="sub">Hang tight — we're checking with your bank. This only takes a moment.</p>
        <div className="pill t-muted">
          <span className="dot pulse" />
          Please don't close this window
        </div>
      </div>
    </main>
  );
}

/* ---- A4 Issuing (auto-advances to the ticket) ------------------------- */
function PayIssuing() {
  return (
    <main className="app">
      <div className="topbar">
        <Wordmark />
        <span className="pill t-good">
          <IconTick />
          Paid
        </span>
      </div>
      <div className="state">
        <div
          style={{
            position: 'relative',
            width: 96,
            height: 96,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <div className="spin" style={{ '--s': '96px' } as CSSProperties} />
          <IconQrGrid size={34} />
        </div>
        <h3>Issuing your ticket…</h3>
        <p className="sub">
          Building your live gate pass. This screen becomes your ticket in a few seconds — no need
          to go anywhere.
        </p>
        <div style={{ width: '100%', maxWidth: 220 }}>
          <div className="track">
            <div className="fill" style={{ width: '68%' }} />
          </div>
        </div>
      </div>
    </main>
  );
}

/* ---- A4-timeout / Done — slow issuance, the ticket lands in chat ------ */
function PayDone() {
  return (
    <main className="app">
      <div className="topbar">
        <Wordmark />
        <span className="pill t-good">
          <IconTick />
          Paid
        </span>
      </div>
      <div className="state">
        <IconTick
          size={60}
          strokeWidth={1.5}
          className="glyph"
          style={{ color: 'var(--good)' }}
          circled
        />
        <h3>
          Your ticket is
          <br />
          on its way
        </h3>
        <p className="sub">
          Payment received. Your live ticket will arrive in your chat in a moment — open it there to
          show your QR at the gate.
        </p>
      </div>
      <OpenChatButton />
    </main>
  );
}

/* ---- A5 Already paid --------------------------------------------------- */
function PayPaid() {
  return (
    <main className="app">
      <div className="topbar">
        <Wordmark />
      </div>
      <div className="state">
        <IconTick
          size={64}
          strokeWidth={1.5}
          className="glyph"
          style={{ color: 'var(--good)' }}
          circled
        />
        <h3>
          You've already
          <br />
          paid for this
        </h3>
        <p className="sub">
          Your ticket is waiting in your chat. Open it there to show your live QR at the gate.
        </p>
      </div>
      <OpenChatButton label="Open my ticket" />
    </main>
  );
}

/* ---- A6 Error ---------------------------------------------------------- */
function PayError({ error }: { error: Error | null }) {
  const status = error instanceof ChatApiError ? error.status : 0;
  const { title, body } =
    status === 401
      ? {
          title: (
            <>
              This payment link
              <br />
              has expired
            </>
          ),
          body: "Fares are held for a short window. Head back to your chat and book again for a fresh link — you haven't been charged.",
        }
      : status === 409
        ? {
            title: (
              <>
                This booking was
                <br />
                already paid
              </>
            ),
            body: 'Your ticket is in your chat, or the quote expired. Check your chat, or book again for a fresh fare.',
          }
        : {
            title: (
              <>
                Couldn't complete
                <br />
                the payment
              </>
            ),
            body: "Something went wrong. Head back to your chat and try again — you haven't been charged.",
          };
  return (
    <main className="app">
      <div className="topbar">
        <Wordmark />
      </div>
      <div className="state">
        <IconWarn className="glyph warn" />
        <h3>{title}</h3>
        <p className="sub">{body}</p>
      </div>
      <BackToChat />
    </main>
  );
}

function formatMoney(paise: number, currency: string): string {
  const rupees = paise / 100;
  const sym = currency && currency !== 'INR' ? `${currency} ` : '₹';
  return `${sym}${rupees.toFixed(2)}`;
}

// Split the fare for the serif hero: the whole rupees big, the paise muted.
function fareParts(paise: number, currency: string): { intPart: string; paisePart: string } {
  const rupees = paise / 100;
  const sym = currency && currency !== 'INR' ? `${currency} ` : '₹';
  const whole = Math.floor(rupees);
  const frac = Math.round((rupees - whole) * 100);
  return { intPart: `${sym}${whole}`, paisePart: `.${String(frac).padStart(2, '0')}` };
}
